import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { Building2, Edit, Trash2, ArrowLeft, Users, CheckCircle, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import { MerchantDetailModal } from './MerchantDetailModal';

import { partnerApi } from '../lib/api/partnerApi';
import { merchantApi } from '../lib/api/merchantApi';
import { PartnerDto, Merchant } from '../lib/api/types';

interface PartnerDetailProps {
  partnerId?: string;
  onEdit?: (partner: PartnerDto) => void;
  onBack?: () => void;
}

export const PartnerDetail: React.FC<PartnerDetailProps> = ({
  partnerId: propPartnerId,
  onEdit,
  onBack,
}) => {
  const { partnerId: paramPartnerId } = useParams<{ partnerId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const partnerId = propPartnerId || paramPartnerId;

  // State for merchant detail modal
  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(null);
  const [isMerchantModalOpen, setIsMerchantModalOpen] = useState(false);

  // Query to fetch partner details
  const {
    data: partnerData,
    isLoading: isLoadingPartner,
    error: partnerError,
  } = useQuery({
    queryKey: ['partners', partnerId],
    queryFn: () => partnerApi.getPartner(partnerId!),
    enabled: !!partnerId,
  });

  // Query to fetch merchants associated with this partner
  const {
    data: merchantData,
    isLoading: isLoadingMerchants,
  } = useQuery({
    queryKey: ['merchants', 'by-partner', partnerId],
    queryFn: () => merchantApi.getAllMerchantsAdmin(partnerId!),
    enabled: !!partnerId,
  });

  // Delete partner mutation
  const deletePartnerMutation = useMutation({
    mutationFn: partnerApi.deletePartner,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      navigate('/partners');
    },
    onError: (error: Error) => {
      console.error('Failed to delete partner:', error);
    },
  });

  const partner = partnerData?.partner;
  const merchants = merchantData?.merchants || [];

  const handleEdit = () => {
    if (partner && onEdit) {
      onEdit(partner);
    } else if (partner) {
      navigate(`/partners/${partner.partnerId}/edit`);
    }
  };

  const handleDelete = () => {
    if (partnerId) {
      deletePartnerMutation.mutate(partnerId);
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate('/partners');
    }
  };

  const handleViewMerchant = (merchant: Merchant) => {
    setSelectedMerchant(merchant);
    setIsMerchantModalOpen(true);
  };

  const handleCloseMerchantModal = () => {
    setIsMerchantModalOpen(false);
    setSelectedMerchant(null);
  };

  if (isLoadingPartner) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-muted-foreground">Loading partner details...</div>
      </div>
    );
  }

  if (partnerError || !partner) {
    return (
      <div className="flex flex-col items-center justify-center p-8 space-y-4">
        <div className="text-destructive">Failed to load partner details</div>
        <Button onClick={handleBack} variant="outline">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Partners
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button onClick={handleBack} variant="outline" size="sm">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{partner.partnerName}</h1>
            <p className="text-muted-foreground">{partner.partnerCode}</p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Button onClick={handleEdit} variant="outline">
            <Edit className="mr-2 h-4 w-4" />
            Edit
          </Button>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive">
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Partner</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete "{partner.partnerName}"?
                  {merchants.length > 0 && (
                    <span className="block mt-2 text-destructive font-medium">
                      Warning: This partner has {merchants.length} associated merchant(s).
                      Deleting this partner may affect those merchants.
                    </span>
                  )}
                  This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  disabled={deletePartnerMutation.isPending}
                >
                  {deletePartnerMutation.isPending ? 'Deleting...' : 'Delete'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Partner Information */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Partner Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Partner Name</label>
              <p className="text-sm">{partner.partnerName}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">Partner Code</label>
              <p className="text-sm font-mono">{partner.partnerCode}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">Biller ID</label>
              <p className="text-sm font-mono">{partner.billerId}</p>
            </div>

            {partner.description && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Description</label>
                <p className="text-sm">{partner.description}</p>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-muted-foreground">Status</label>
              <div className="flex items-center gap-2">
                {partner.isActive ? (
                  <>
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm text-green-700">Active</span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4 text-red-500" />
                    <span className="text-sm text-red-700">Inactive</span>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Associated Merchants */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Associated Merchants ({merchants.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoadingMerchants ? (
              <div className="text-center text-muted-foreground py-4">
                Loading merchants...
              </div>
            ) : merchants.length === 0 ? (
              <div className="text-center text-muted-foreground py-4">
                No merchants associated with this partner
              </div>
            ) : (
              <div className="space-y-3">
                {merchants.slice(0, 5).map((merchant: any) => (
                  <div
                    key={merchant.id}
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <div>
                      <p className="font-medium">{merchant.merchantName}</p>
                      <p className="text-sm text-muted-foreground">
                        Account: {merchant.accountNumber}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        ID: {merchant.id}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewMerchant(merchant)}
                    >
                      View
                    </Button>
                  </div>
                ))}

                {merchants.length > 5 && (
                  <div className="text-center pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/merchants?partnerId=${partnerId}`)}
                    >
                      View All {merchants.length} Merchants
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Merchant Detail Modal */}
      <MerchantDetailModal
        merchant={selectedMerchant}
        isOpen={isMerchantModalOpen}
        onClose={handleCloseMerchantModal}
      />
    </div>
  );
};
